import React, { Component } from 'react'

import SharedModal from 'tailwind/common/components/SharedModal'
import ProgressBar from 'tailwind/common/components/ProgressBar'
import BusinessSidebar from 'business/direct_quote_request/components/BusinessSidebar'
import BusinessSidebarCompact from 'business/direct_quote_request/components/BusinessSidebarCompact'
import MobileWizardHeader from 'business/direct_quote_request/components/MobileWizardHeader'
import NavigationButtons from 'business/direct_quote_request/components/NavigationButtons'
import ScrollDownIndicator from 'tailwind/common/components/ScrollDownIndicator'
import StepHeader, { WizardCloseButton } from 'business/direct_quote_request/components/StepHeader'
import JobDetails from 'business/direct_quote_request/components/steps/JobDetails'
import Timeline from 'business/direct_quote_request/components/steps/Timeline'
import AccountAndNotifications from 'business/direct_quote_request/components/steps/AccountAndNotifications'
import PhoneOtpVerification from 'business/direct_quote_request/components/steps/PhoneOtpVerification'
import PageOtpShell from 'business/direct_quote_request/components/PageOtpShell'
import PrePostJobCheckPanel from 'business/direct_quote_request/components/PrePostJobCheckPanel'
import {
  MIN_DESCRIPTION_LENGTH,
  DQR_MODAL_MAX_WIDTH_ACCOUNT_ONLY_CLASS,
  DQR_MODAL_MAX_WIDTH_WITH_SIDEBAR_CLASS,
  DQR_SUBCATEGORY_SELECTION_ERROR,
  DQR_SUBURB_SELECTION_ERROR,
  dqrSubcategoryServiceabilityError,
  dqrSuburbServiceabilityError,
  formatRespondsLabel,
  maskBusinessPhoneForDisplay,
} from 'business/direct_quote_request/constants'
import { buildJobCreateIncluded } from 'business/direct_quote_request/jobCreateIncluded'
import API from 'common/src/api'
import coreUtils from 'common/src/utils/coreUtils'
import {
  fetchPhoneVerificationRequired,
  needsPhoneVerificationGate,
  phoneVerificationGateOpensChangePhone,
  refreshUserPhoneStatus,
  runWhenPhoneVerificationRequiredReady,
} from 'business/direct_quote_request/phoneVerification'

/**
 * Direct Quote Request wizard — shared by the modal and full-page flows.
 *
 * Changes here affect both surfaces unless gated behind `displayMode === 'page'`
 * or page-only CSS under `app/client/business/direct_quote_request/page/`.
 *
 * - Modal: `DirectQuoteRequestWizardHost` (business profile CTA)
 * - Full page: `app/client/business/direct_quote_request/page/src/index.js`
 *   Example: `/business/:id/request-quote` (e.g. `/business/11111362/request-quote`)
 *
 * Page layout overrides live in `page/styles/index.scss`, not in this file.
 */
const MODAL_ID = 'direct-quote-request-wizard-modal'

const JOB_SUBMIT_CONTACT_BLOCKED_DETAIL = 'Unable to find the user mentioned'
const JOB_SUBMIT_CONTACT_BLOCKED_MESSAGE =
  "This contact number can't be used to post a job. Please use a different number."

const initialFormState = {
  subcategoryId: null,
  subcategoryName: '',
  suburbId: null,
  suburbName: '',
  description: '',
  broadcastToOthers: false,
  timingPreference: '',
  scheduleDate: '',
  attachments: [],
  email: '',
}

const buildInitialFormState = (business = {}) => ({
  ...initialFormState,
  subcategoryId: business.prefilledSubcategoryId || null,
  subcategoryName: business.prefilledSubcategoryName || '',
  suburbId: business.prefilledSuburbId || null,
  suburbName: business.prefilledSuburbName || '',
})

export default class DirectQuoteRequestWizard extends Component {
  constructor(props) {
    super(props)

    this.state = {
      currentStep: 0,
      saving: false,
      errorMessage: null,
      jobSubmitError: null,
      accountAuthStage: 'email',
      accountResumeStage: null,
      accountResumeError: null,
      formSessionKey: 0,
      form: buildInitialFormState(props.business),
      stepScrollEl: null,
      prePostCheckError: '',
      duplicateJobId: 0,
      phoneVerificationGateActive: false,
      phoneGateOpenChangePhone: false,
      phoneGateChangePhoneError: '',
      phoneGateInitialSetupComplete: false,
      phoneGateInitialVerificationStatus: '',
      checkingServiceability: false,
      serviceabilityBlock: null,
    }
  }

  setStepScrollRef = (el) => {
    if (el !== this.state.stepScrollEl) {
      this.setState({ stepScrollEl: el })
    }
  }

  scrollModalOverlayToTop = () => {
    const modalEl = document.getElementById(MODAL_ID)
    if (modalEl) {
      modalEl.scrollTop = 0
    }

    if ((this.props.displayMode || 'modal') === 'page') {
      window.scrollTo(0, 0)
    }
  }

  componentDidMount() {
    if (this.state.form.suburbId) {
      fetchPhoneVerificationRequired(this.state.form.suburbId)
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevProps.isOpen && this.props.isOpen) {
      if (this.props.currentUserPresent) {
        refreshUserPhoneStatus()
      }

      if (this.state.form.suburbId) {
        fetchPhoneVerificationRequired(this.state.form.suburbId)
      }
    }

    if (prevProps.isOpen && !this.props.isOpen) {
      this.resetWizard()
    }

    if (prevState.form.suburbId !== this.state.form.suburbId) {
      fetchPhoneVerificationRequired(this.state.form.suburbId)
    }

    if (prevProps.currentUserPresent !== this.props.currentUserPresent) {
      const steps = this.steps()
      if (this.state.currentStep >= steps.length) {
        this.setState({ currentStep: Math.max(0, steps.length - 1) })
      }
    }

    if (
      prevState.currentStep !== this.state.currentStep ||
      prevState.formSessionKey !== this.state.formSessionKey ||
      prevState.accountAuthStage !== this.state.accountAuthStage
    ) {
      if (this.state.stepScrollEl) {
        this.state.stepScrollEl.scrollTop = 0
      }
      this.scrollModalOverlayToTop()
    }
  }

  resetWizard = () => {
    this.setState((prevState) => ({
      currentStep: 0,
      saving: false,
      errorMessage: null,
      jobSubmitError: null,
      accountAuthStage: 'email',
      accountResumeStage: null,
      accountResumeError: null,
      prePostCheckError: '',
      duplicateJobId: 0,
      phoneVerificationGateActive: false,
      phoneGateOpenChangePhone: false,
      phoneGateChangePhoneError: '',
      phoneGateInitialSetupComplete: false,
      phoneGateInitialVerificationStatus: '',
      checkingServiceability: false,
      serviceabilityBlock: null,
      formSessionKey: prevState.formSessionKey + 1,
      form: buildInitialFormState(this.props.business),
    }))
  }

  handleAccountAuthStageChange = (accountAuthStage) => {
    window.requestAnimationFrame(() => {
      this.setState({ accountAuthStage })
    })
  }

  handleFieldChange = (fieldName, value) => {
    this.setState((prevState) => {
      const nextForm = { ...prevState.form, [fieldName]: value }
      const nextState = { form: nextForm }
      const block = prevState.serviceabilityBlock

      if (!block) {
        return nextState
      }

      const subcategoryChanged = (
        (fieldName === 'subcategoryId' || fieldName === 'subcategoryName') &&
        (nextForm.subcategoryId !== block.subcategoryId ||
          nextForm.subcategoryName !== block.subcategoryName)
      )
      const suburbChanged = (
        (fieldName === 'suburbId' || fieldName === 'suburbName') &&
        (nextForm.suburbId !== block.suburbId || nextForm.suburbName !== block.suburbName)
      )

      let nextBlock = block

      if (subcategoryChanged && block.subcategoryError) {
        nextBlock = { ...nextBlock, subcategoryError: '' }
      }

      if (suburbChanged && block.suburbError) {
        nextBlock = { ...nextBlock, suburbError: '' }
      }

      if (!nextBlock.subcategoryError && !nextBlock.suburbError) {
        nextState.serviceabilityBlock = null
      } else {
        nextState.serviceabilityBlock = nextBlock
      }

      return nextState
    })
  }

  isServiceabilityBlocked = () => {
    const block = this.state.serviceabilityBlock
    if (!block) return false

    return Boolean(block.subcategoryError || block.suburbError)
  }

  buildServiceabilityBlock = ({
    form,
    subcategoryError = '',
    suburbError = '',
  }) => ({
    subcategoryId: form.subcategoryId,
    subcategoryName: form.subcategoryName,
    suburbId: form.suburbId,
    suburbName: form.suburbName,
    subcategoryError,
    suburbError,
  })

  advanceFromJobDetailsStep = () => {
    this.setState((prevState) => ({ currentStep: prevState.currentStep + 1 }))
  }

  validateJobDetailsServiceability = () => {
    const { form } = this.state
    const { business } = this.props
    const businessName = (business && business.businessName) || 'This business'

    const subcategoryError = form.subcategoryId ? '' : DQR_SUBCATEGORY_SELECTION_ERROR
    const suburbError = form.suburbId ? '' : DQR_SUBURB_SELECTION_ERROR

    if (subcategoryError || suburbError) {
      this.setState({
        serviceabilityBlock: this.buildServiceabilityBlock({
          form,
          subcategoryError,
          suburbError,
        }),
      })
      return
    }

    if (!business || !business.businessId) {
      this.advanceFromJobDetailsStep()
      return
    }

    this.setState({ checkingServiceability: true })

    $.ajax(API.V3.directQuoteRequestServiceabilityGet({
      businessId: business.businessId,
      suburbId: form.suburbId,
      subCategoryId: form.subcategoryId,
    }))
      .done((response) => {
        const attrs = response && response.data && response.data.attributes

        if (!attrs) {
          this.setState({ checkingServiceability: false, serviceabilityBlock: null }, () => {
            this.advanceFromJobDetailsStep()
          })
          return
        }

        // `?? `-free on purpose: webpack 4 (webpacker 5) cannot parse ES2020,
        // and `!= null` covers exactly null/undefined. `||` would be wrong for
        // these booleans (`false || y` diverges from `false ?? y`).
        const subcategoryServiceable = (
          attrs.subcategoryServiceable != null
            ? attrs.subcategoryServiceable
            : attrs.subcategory_serviceable
        )
        const locationServiceable = (
          attrs.locationServiceable != null
            ? attrs.locationServiceable
            : attrs.location_serviceable
        )

        if (subcategoryServiceable && locationServiceable) {
          this.setState({ checkingServiceability: false, serviceabilityBlock: null }, () => {
            this.advanceFromJobDetailsStep()
          })
          return
        }

        this.setState({
          checkingServiceability: false,
          serviceabilityBlock: this.buildServiceabilityBlock({
            form,
            subcategoryError: subcategoryServiceable
              ? ''
              : dqrSubcategoryServiceabilityError(businessName),
            suburbError: locationServiceable
              ? ''
              : dqrSuburbServiceabilityError(businessName),
          }),
        })
      })
      .fail(() => {
        this.setState({ checkingServiceability: false, serviceabilityBlock: null }, () => {
          this.advanceFromJobDetailsStep()
        })
      })
  }

  steps = () => {
    const { currentUserPresent } = this.props
    const stepsList = [
      {
        key: 'job_details',
        component: JobDetails,
        eyebrow: 'Location & job details:',
        title: 'Tell us about your job',
        isValid: this.jobDetailsValid(),
      },
      {
        key: 'timeline',
        component: Timeline,
        eyebrow: 'Schedule and availability:',
        title: 'Set Your Timeline',
        isValid: this.timelineValid(),
      },
    ]

    if (!currentUserPresent) {
      stepsList.push({
        key: 'account',
        component: AccountAndNotifications,
        isValid: true,
        hideNavigation: true,
      })
    }

    return stepsList
  }

  jobDetailsValid = () => {
    const { subcategoryName, suburbName, description } = this.state.form

    return (
      subcategoryName.trim().length > 0 &&
      suburbName.trim().length > 0 &&
      description.trim().length >= MIN_DESCRIPTION_LENGTH
    )
  }

  timelineValid = () => {
    const { timingPreference, scheduleDate } = this.state.form

    if (timingPreference === 'on_a_particular_date') {
      return scheduleDate.length > 0
    }

    return timingPreference.length > 0
  }

  handleContinue = () => {
    const { currentStep } = this.state
    const { currentUserPresent } = this.props
    const steps = this.steps()
    const currentStepConfig = steps[currentStep]
    const isLastStep = currentStep === steps.length - 1

    if (currentStepConfig.hideNavigation) {
      return
    }

    if (isLastStep && currentUserPresent) {
      this.beginSubmit()
      return
    }

    if (currentStepConfig.key === 'job_details') {
      this.validateJobDetailsServiceability()
      return
    }

    this.setState((prevState) => ({ currentStep: prevState.currentStep + 1 }))
  }

  handleAuthSuccess = () => {
    this.beginSubmit()
  }

  handleAccountResumeHandled = () => {
    this.setState({ accountResumeStage: null, accountResumeError: null })
  }

  handleJobSubmitErrorHandled = () => {
    this.setState({ jobSubmitError: null })
  }

  isOnAccountStep = () => {
    const steps = this.steps()
    const stepConfig = steps[this.state.currentStep]

    return stepConfig && stepConfig.key === 'account'
  }

  jobSubmitErrorMessage = (detail) => {
    if (detail === JOB_SUBMIT_CONTACT_BLOCKED_DETAIL) {
      return JOB_SUBMIT_CONTACT_BLOCKED_MESSAGE
    }

    return detail || 'Something went wrong. Please try again.'
  }

  isContactBlockedJobError = (detail) => detail === JOB_SUBMIT_CONTACT_BLOCKED_DETAIL

  handleBack = () => {
    this.setState((prevState) => ({
      currentStep: Math.max(0, prevState.currentStep - 1),
      accountAuthStage: 'email',
    }))
  }

  buildPrePostCheckError = ({ dailyJobCountExceeded, duplicateJobExists }) => {
    if (dailyJobCountExceeded) return 'TOO_MANY_JOB_REQUESTS'
    if (duplicateJobExists) return 'DUPLICATE_JOB'

    return ''
  }

  beginSubmit = ({ skipPhoneGate = false } = {}) => {
    if (!skipPhoneGate) {
      this.ensurePhoneVerifiedBeforeSubmit()
      return
    }

    const { suburbId, subcategoryId } = this.state.form

    if (!suburbId || !subcategoryId) {
      this.submitRequest()
      return
    }

    this.setState({
      saving: true,
      prePostCheckError: '',
      duplicateJobId: 0,
      errorMessage: null,
      jobSubmitError: null,
    })

    $.ajax(API.V3.prePostJobCheck({
      suburbId,
      subCategoryId: subcategoryId,
    }))
      .done((response) => {
        const meta = (response && response.meta) || {}
        const prePostCheckError = this.buildPrePostCheckError({
          dailyJobCountExceeded: meta.daily_job_count_exceeded,
          duplicateJobExists: meta.duplicate_job_exists,
        })

        if (prePostCheckError === '') {
          this.submitRequest()
          return
        }

        this.setState({
          saving: false,
          prePostCheckError,
          duplicateJobId: meta.duplicate_job_id || 0,
        })
      })
      .fail(() => {
        this.submitRequest()
      })
  }

  ensurePhoneVerifiedBeforeSubmit = () => {
    const { suburbId } = this.state.form

    this.setState({
      saving: true,
      prePostCheckError: '',
      duplicateJobId: 0,
      errorMessage: null,
      jobSubmitError: null,
    })

    const proceedAfterPhoneCheck = () => {
      runWhenPhoneVerificationRequiredReady(() => {
        refreshUserPhoneStatus().always(() => {
          if (needsPhoneVerificationGate()) {
            this.preparePhoneVerificationGate()
            return
          }

          this.beginSubmit({ skipPhoneGate: true })
        })
      })
    }

    if (suburbId) {
      fetchPhoneVerificationRequired(suburbId)
    }

    proceedAfterPhoneCheck()
  }

  openPhoneVerificationGate = ({
    openChangePhone = false,
    changePhoneError = '',
    initialSetupComplete = false,
    initialVerificationStatus = '',
  } = {}) => {
    this.setState({
      saving: false,
      phoneVerificationGateActive: true,
      phoneGateOpenChangePhone: openChangePhone,
      phoneGateChangePhoneError: openChangePhone
        ? (changePhoneError || this.state.phoneGateChangePhoneError)
        : '',
      phoneGateInitialSetupComplete: initialSetupComplete,
      phoneGateInitialVerificationStatus: initialVerificationStatus,
    })
  }

  preparePhoneVerificationGate = () => {
    if (phoneVerificationGateOpensChangePhone()) {
      this.openPhoneVerificationGate({ openChangePhone: true })
      return
    }

    $.ajax(API.V3.phoneVerificationGenerate())
      .done((data) => {
        const status = data.verification_status

        if (status === 'verified') {
          window.App = window.App || {}
          window.App.userPhoneVerified = true
          this.beginSubmit({ skipPhoneGate: true })
          return
        }

        if (status === 'unavailable' || status === 'skipped') {
          window.App = window.App || {}
          window.App.userPhoneIsValid = false
          window.App.userPhoneVerified = false

          if (coreUtils.phoneVerificationRequired()) {
            this.openPhoneVerificationGate({
              openChangePhone: true,
              changePhoneError: 'Please enter a valid mobile number',
            })
            return
          }

          this.openPhoneVerificationGate({
            initialSetupComplete: true,
            initialVerificationStatus: status,
          })
          return
        }

        this.openPhoneVerificationGate({ initialSetupComplete: true })
      })
      .fail(() => {
        // Avoid remounting into another setup attempt that can stick on a blank spinner.
        if (coreUtils.phoneVerificationRequired()) {
          this.openPhoneVerificationGate({
            openChangePhone: true,
            changePhoneError:
              'Sorry, we could not send a verification code. Please check your mobile number and try again.',
          })
          return
        }

        this.openPhoneVerificationGate({ initialSetupComplete: false })
      })
  }

  handlePhoneGateVerified = () => {
    this.setState({
      phoneVerificationGateActive: false,
      phoneGateOpenChangePhone: false,
      phoneGateChangePhoneError: '',
      phoneGateInitialSetupComplete: false,
      phoneGateInitialVerificationStatus: '',
      saving: true,
    }, () => {
      this.beginSubmit({ skipPhoneGate: true })
    })
  }

  handlePhoneGateUnavailable = () => {
    window.App = window.App || {}
    window.App.userPhoneIsValid = false
    window.App.userPhoneVerified = false

    if (coreUtils.phoneVerificationRequired()) {
      this.setState({
        phoneGateOpenChangePhone: true,
        phoneGateChangePhoneError: 'Please enter a valid mobile number',
      })
    }
  }

  handlePhoneGatePhoneUpdated = () => {
    this.setState({
      phoneGateOpenChangePhone: false,
      phoneGateChangePhoneError: '',
    })
  }

  handlePhoneGateChangePhoneDismissed = () => {
    this.setState({
      phoneGateOpenChangePhone: false,
      phoneGateChangePhoneError: '',
    })
  }

  renderPhoneVerificationGate = () => {
    const {
      saving,
      phoneGateOpenChangePhone,
      phoneGateChangePhoneError,
      phoneGateInitialSetupComplete,
      phoneGateInitialVerificationStatus,
    } = this.state
    const isPage = (this.props.displayMode || 'modal') === 'page'

    const otpPanel = (
      <PhoneOtpVerification
        active
        saving={saving}
        initialSetupComplete={phoneGateInitialSetupComplete}
        initialVerificationStatus={phoneGateInitialVerificationStatus}
        openChangePhone={phoneGateOpenChangePhone}
        changePhoneError={phoneGateChangePhoneError}
        onClose={isPage ? null : this.handleClose}
        onVerified={this.handlePhoneGateVerified}
        onUnavailable={this.handlePhoneGateUnavailable}
        onPhoneUpdated={this.handlePhoneGatePhoneUpdated}
        onChangePhoneDismissed={this.handlePhoneGateChangePhoneDismissed}
      />
    )

    if (isPage) {
      return (
        <PageOtpShell backLink={this.props.pageBackLink}>
          {otpPanel}
        </PageOtpShell>
      )
    }

    return (
      <div className="flex flex-col px-4 pt-6 pb-4 max-md:flex-1 max-md:min-h-0 max-md:h-full md:flex-none md:h-auto lg:px-12 lg:pt-8 lg:pb-6">
        {otpPanel}
      </div>
    )
  }

  submitRequest = () => {
    this.setState({
      saving: true,
      errorMessage: null,
      jobSubmitError: null,
      accountResumeStage: null,
      accountResumeError: null,
    })

    const jobCreateData = this.buildJobCreatePayload()

    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({ event: 'custom.event.submitJob' })

    $.ajax(API.V3.jobCreate(jobCreateData))
      .done((createdJson) => {
        const jobId = createdJson && createdJson.data && createdJson.data.id
        if (!jobId) {
          const message = 'Job could not be created.'
          if (this.isOnAccountStep()) {
            this.setState({ saving: false, jobSubmitError: message })
          } else {
            this.setState({ saving: false, errorMessage: message })
          }
          return
        }

        this.uploadAttachmentsToJob(jobId)
          .then(() => {
            this.redirectToJobPage(jobId)
          })
          .catch(() => {
            this.redirectToJobPage(jobId)
          })
      })
      .fail((xhr) => {
        let detail
        try {
          const json = xhr && xhr.responseJSON
          if (json && json.errors && json.errors[0] && json.errors[0].detail) {
            detail = json.errors[0].detail
          }
        } catch (e) {}

        const message = this.jobSubmitErrorMessage(detail)

        if (this.isContactBlockedJobError(detail)) {
          const onAccountStep = this.isOnAccountStep()

          this.setState({
            saving: false,
            accountResumeStage: 'change_phone',
            accountResumeError: message,
            phoneVerificationGateActive: !onAccountStep,
            phoneGateOpenChangePhone: true,
            phoneGateChangePhoneError: message,
          })
          return
        }

        if (this.isOnAccountStep()) {
          this.setState({ saving: false, jobSubmitError: message })
          return
        }

        this.setState({ saving: false, errorMessage: message })
      })
  }

  redirectToJobPage = (jobId) => {
    const { form } = this.state
    const distributed = form.broadcastToOthers ? '1' : '0'

    window.location.href = `/jobs/${jobId}?successful_job=true&dqr=1&dqr_distributed=${distributed}`
  }

  buildJobCreatePayload = () => {
    const { business } = this.props
    const { form } = this.state

    const subcats = form.subcategoryId
      ? [{ type: 'subcategories', id: form.subcategoryId }]
      : []

    const schedule = form.timingPreference || 'flexible'
    let description = form.description

    if (form.timingPreference === 'on_a_particular_date' && form.scheduleDate) {
      description += `\n\nSchedule: Need job done on ${form.scheduleDate}\n\n`
    }

    return {
      data: {
        type: 'jobs',
        attributes: {
          description: description,
          schedule: schedule,
          starting: schedule.replace(/_/g, ' '),
          can_call: true,
          disable_processing: false,
          direct_quote_request_business_id: business && business.businessId,
          distribute_to_others: Boolean(form.broadcastToOthers),
        },
        relationships: {
          suburb: {
            data: { type: 'suburbs', id: form.suburbId },
          },
          'job-subcategories': { data: subcats },
          'exclusive-to-business': {
            data: { type: 'businesses', id: business && business.businessId },
          },
        },
      },
      included: buildJobCreateIncluded(),
    }
  }

  handleClose = () => {
    this.props.onClose()
  }

  uploadAttachmentsToJob = (jobId) => {
    const { form } = this.state
    const files = form.attachments || []

    if (!files.length) return Promise.resolve()

    const uploadOne = (file) => {
      return new Promise((resolve, reject) => {
        const formData = new FormData()
        const apiData = JSON.stringify(API.V3.build.attachment({ jobId: jobId }))

        formData.append('attachment', apiData)
        formData.delete && formData.delete('file')
        formData.append('file', file)

        $.ajax(API.V3.attachment({ data: formData }))
          .done(() => resolve())
          .fail(() => reject())
      })
    }

    return files.reduce((p, file) => p.then(() => uploadOne(file)), Promise.resolve())
  }

  sidebarProps = () => {
    const { business, currentUserPresent, displayMode } = this.props
    const isPage = (displayMode || 'modal') === 'page'
    const showFullPhone = currentUserPresent && Boolean(business.contactPhone)
    const displayPhone = showFullPhone
      ? business.contactPhone
      : maskBusinessPhoneForDisplay(business.contactPhone)

    return {
      businessName: business.businessName,
      suburbName: business.jobFilterSuburbName || business.suburbName,
      rating: business.rating,
      reviews: business.reviews,
      hasFeedbackScore: business.hasFeedbackScore,
      hasLogo: business.hasLogo,
      logoUrl: business.logoUrl,
      logoInitials: business.logoInitials,
      logoColor: business.logoColor,
      displayPhone,
      bookingPath: business.bookingPath,
      canCall: showFullPhone,
      canBook: business.showMeetingLink && Boolean(business.bookingPath),
      userName: business.userName,
      respondsLabel: formatRespondsLabel(business.responseTime),
      backToUrl: isPage ? business.backToUrl : null,
      backToLabel: isPage ? business.backToLabel : null,
    }
  }

  renderPagePanelBackLink = (hideSidebar) => {
    if (!this.props.pageBackLink) return null

    const isPage = (this.props.displayMode || 'modal') === 'page'
    const hideOnDesktopSidebar = isPage && !hideSidebar

    return (
      <div className={['shrink-0', hideOnDesktopSidebar ? 'md:hidden' : ''].join(' ')}>
        {this.props.pageBackLink}
      </div>
    )
  }

  renderStepContent = () => {
    const { currentStep, form, formSessionKey, serviceabilityBlock } = this.state

    const steps = this.steps()
    const safeStep = Math.min(currentStep, Math.max(0, steps.length - 1))
    const stepConfig = steps[safeStep]

    if (!stepConfig) return null

    const StepComponent = stepConfig.component
    const { business } = this.props

    if (stepConfig.key === 'account') {
      const { accountAuthStage } = this.state
      const hideSidebar = accountAuthStage !== 'email'

      return (
        <AccountAndNotifications
          key={`account-${formSessionKey}`}
          email={form.email}
          suburbId={form.suburbId}
          saving={this.state.saving}
          jobSubmitError={this.state.jobSubmitError}
          resumeStage={this.state.accountResumeStage}
          resumeError={this.state.accountResumeError}
          scrollInParent={hideSidebar && accountAuthStage !== 'otp'}
          onClose={this.props.displayMode === 'page' ? null : this.handleClose}
          pageBackLink={
            hideSidebar && this.props.displayMode === 'page'
              ? this.props.pageBackLink
              : null
          }
          onFieldChange={this.handleFieldChange}
          onBack={this.handleBack}
          onAuthSuccess={this.handleAuthSuccess}
          onAuthStageChange={this.handleAccountAuthStageChange}
          onResumeHandled={this.handleAccountResumeHandled}
          onJobSubmitErrorHandled={this.handleJobSubmitErrorHandled}
          isPage={this.props.displayMode === 'page'}
        />
      )
    }

    return (
      <StepComponent
        key={`${stepConfig.key}-${formSessionKey}`}
        {...form}
        businessName={business && business.businessName}
        subcategoryError={serviceabilityBlock && serviceabilityBlock.subcategoryError}
        suburbError={serviceabilityBlock && serviceabilityBlock.suburbError}
        stepScrollEl={this.state.stepScrollEl}
        onFieldChange={this.handleFieldChange}
      />
    )
  }

  render() {
    const displayMode = this.props.displayMode || 'modal'
    const isPage = displayMode === 'page'
    const { isOpen } = this.props

    if (!isOpen && !isPage) {
      return (
        <SharedModal
          id={MODAL_ID}
          isOpen={false}
          maxWidth={DQR_MODAL_MAX_WIDTH_WITH_SIDEBAR_CLASS}
          innerPadding="p-0"
          bgColor="bg-surface"
          surfaceClassName="rounded-20 shadow-md overflow-hidden"
        />
      )
    }

    const {
      currentStep,
      saving,
      errorMessage,
      accountAuthStage,
      prePostCheckError,
      duplicateJobId,
      form,
      phoneVerificationGateActive,
      checkingServiceability,
    } = this.state
    const steps = this.steps()
    const safeStep = Math.min(currentStep, Math.max(0, steps.length - 1))
    const currentStepConfig = steps[safeStep]

    if (!currentStepConfig) {
      return null
    }

    const showPrePostJobCheck = Boolean(prePostCheckError)
    const showPhoneVerificationGate = phoneVerificationGateActive
    const showOverlayContent = showPrePostJobCheck || showPhoneVerificationGate
    const progressValue = steps.length ? safeStep + 1 : 0
    const hideSidebar = showPhoneVerificationGate ||
      (currentStepConfig.key === 'account' && accountAuthStage !== 'email')
    const showMobileBusinessCard = !hideSidebar && !showOverlayContent
    const modalMaxWidth = (currentStepConfig.key === 'account' || showPhoneVerificationGate)
      ? (hideSidebar ? DQR_MODAL_MAX_WIDTH_ACCOUNT_ONLY_CLASS : DQR_MODAL_MAX_WIDTH_WITH_SIDEBAR_CLASS)
      : DQR_MODAL_MAX_WIDTH_WITH_SIDEBAR_CLASS
    const contentPanelRadiusClass = hideSidebar ? 'rounded-20' : 'md:rounded-r-20'
    const contentPanelWidthClass = hideSidebar ? '' : 'md:w-96 lg:w-[600px] md:shrink-0'
    const hideNavigation = currentStepConfig.hideNavigation || showOverlayContent
    const useOverlayScroll = hideSidebar && hideNavigation && !showPrePostJobCheck && (
      isPage
        ? true
        : (currentStepConfig.key === 'account' || showPhoneVerificationGate)
    )
    const panelAutoHeight = useOverlayScroll
    const panelAutoHeightPanelClass = panelAutoHeight
      ? isPage
        ? 'md:flex-none md:h-auto md:overflow-visible'
        : 'max-md:flex-1 md:flex-none md:h-auto overflow-hidden md:overflow-visible'
      : null
    const panelAutoHeightInnerClass = panelAutoHeight
      ? isPage
        ? 'md:flex-none md:h-auto md:overflow-visible'
        : 'max-md:flex-1 max-md:min-h-0 overflow-hidden md:flex-none md:h-auto md:overflow-visible'
      : null
    const panelAutoHeightHostClass = panelAutoHeight
      ? isPage
        ? 'md:flex-none md:h-auto md:overflow-visible'
        : 'max-md:flex-1 max-md:h-full md:flex-none md:h-auto md:overflow-visible'
      : null
    const continueDisabled = !currentStepConfig.isValid ||
      this.isServiceabilityBlocked() ||
      checkingServiceability
    const navigationSaving = saving || checkingServiceability
    const showStepError = errorMessage && currentStepConfig.key !== 'account' && !showOverlayContent
    const prePostJobCheckPanel = (
      <PrePostJobCheckPanel
        errorMessage={prePostCheckError}
        duplicateJobId={duplicateJobId}
        categoryName={form.subcategoryName}
        suburbName={form.suburbName}
      />
    )

    const pagePrePostJobCheckPanel = isPage ? (
      hideSidebar ? (
        <div className="dqr-page-pre-post-shell flex flex-1 flex-col min-h-0 h-full w-full">
          {this.renderPagePanelBackLink(hideSidebar)}
          {prePostJobCheckPanel}
        </div>
      ) : (
        prePostJobCheckPanel
      )
    ) : prePostJobCheckPanel

    const modalHeightClass = isPage
      ? ''
      : showPrePostJobCheck
        ? 'min-h-[70vh] md:h-[min(838px,85vh)]'
        : 'md:h-[min(838px,85vh)]'

    const wizardBody = (
        <div
          className={[
            'relative flex flex-col md:flex-row min-h-0',
            useOverlayScroll
              ? [
                  isPage
                    ? 'md:max-h-none md:h-auto md:overflow-visible'
                    : 'max-md:h-full overflow-hidden md:h-auto md:max-h-none md:overflow-visible md:items-start',
                ].join(' ')
              : modalHeightClass,
            isPage && useOverlayScroll ? 'dqr-page-overlay-scroll' : '',
            isPage && hideSidebar ? 'dqr-page-no-sidebar' : '',
            isPage && showPrePostJobCheck ? 'dqr-page-centred-overlay' : '',
          ].join(' ')}
        >
            <div
              className={
                hideSidebar
                  ? 'hidden'
                  : 'hidden md:flex md:h-full md:flex-shrink-0 md:min-h-0 md:self-stretch overflow-hidden transition-all duration-200'
              }
              aria-hidden={hideSidebar}
            >
              <BusinessSidebar {...this.sidebarProps()} />
            </div>

            <div className={[
              'relative z-0 flex flex-col min-h-0 min-w-0 bg-surface w-full',
              panelAutoHeightPanelClass || 'flex-1 overflow-hidden',
              contentPanelRadiusClass,
              contentPanelWidthClass,
            ].join(' ')}
            >
              <div className={[
                'shrink-0',
                hideSidebar ? 'hidden' : ''].join(' ')}>
                <ProgressBar
                  value={progressValue}
                  max={steps.length}
                  barClassName="bg-base-green-main"
                  heightClass="h-1.5"
                  roundedClass="rounded-none"
                />
              </div>

              <div className="md:hidden absolute right-2 top-[22px] z-10">
                <WizardCloseButton onClick={this.handleClose} />
              </div>

              {/* Account step */}
              <div
                className={[
                  'flex flex-col min-h-0',
                  panelAutoHeightInnerClass || [
                    'flex flex-1 flex-col min-h-0',
                    showPrePostJobCheck ? 'h-full' : '',
                    hideNavigation ? 'overflow-hidden' : 'overflow-y-auto overflow-x-hidden md:overflow-hidden',
                  ].join(' '),
                ].join(' ')}
              >
                {showMobileBusinessCard && (!hideNavigation || accountAuthStage === 'email') && (
                  <div className="md:hidden shrink-0 p-4 flex flex-col gap-2">
                    <MobileWizardHeader />
                    <BusinessSidebarCompact {...this.sidebarProps()} />
                  </div>
                )}

                <div
                  className={[
                    'flex flex-col',
                    panelAutoHeightInnerClass || (
                      hideNavigation
                        ? showPrePostJobCheck
                          ? 'flex-1 min-h-0 h-full overflow-hidden'
                          : 'flex-1 min-h-0 overflow-hidden'
                        : 'flex-1 min-h-0 gap-4 px-4 pt-2 pb-6 md:gap-4 md:px-4 md:py-8 md:pb-4 lg:p-12 lg:pb-4 xl:pb-6 lg:gap-6 md:overflow-hidden'
                    ),
                  ].join(' ')}
                >
                  {!hideNavigation && this.renderPagePanelBackLink(hideSidebar)}

                  {!hideNavigation && (
                    <StepHeader
                      className="shrink-0"
                      eyebrow={currentStepConfig.eyebrow}
                      title={currentStepConfig.title}
                      action={(
                        <div className="flex w-full items-center justify-end gap-2">
                          <ScrollDownIndicator
                            inline
                            scrollEl={this.state.stepScrollEl}
                            contentKey={`${currentStepConfig.key}-${this.state.formSessionKey}`}
                          />
                          <WizardCloseButton compact onClick={this.handleClose} />
                        </div>
                      )}
                    />
                  )}

                  {hideNavigation ? (
                    <div className={[
                      'flex flex-col min-h-0',
                      isPage ? 'dqr-page-account-step-host' : '',
                      panelAutoHeightHostClass || 'flex flex-1 flex-col min-h-0 h-full',
                    ].join(' ')}
                    >
                      {showPrePostJobCheck && !isPage && (
                        <div className="hidden md:flex shrink-0 justify-end px-4 pt-8 lg:px-12">
                          <WizardCloseButton onClick={this.handleClose} />
                        </div>
                      )}
                      {showPrePostJobCheck
                        ? pagePrePostJobCheckPanel
                        : showPhoneVerificationGate
                          ? this.renderPhoneVerificationGate()
                          : this.renderStepContent()}
                    </div>
                  ) : (
                    <div
                      ref={this.setStepScrollRef}
                      className="flex flex-1 flex-col min-h-0 md:overflow-y-auto md:overflow-x-hidden md:pr-4"
                    >
                      <div className="flex flex-col gap-4 lg:gap-6">
                        {showPrePostJobCheck
                        ? pagePrePostJobCheckPanel
                        : showPhoneVerificationGate
                          ? this.renderPhoneVerificationGate()
                          : this.renderStepContent()}
                      </div>
                    </div>
                  )}

                  {showStepError && (
                    <p className="shrink-0 text-sm text-base-red-900 md:hidden">{errorMessage}</p>
                  )}

                  {!hideNavigation && (
                    <div className="shrink-0 pt-2 md:hidden">
                      <NavigationButtons
                        currentStep={safeStep}
                        onBack={this.handleBack}
                        onContinue={this.handleContinue}
                        isContinueDisabled={continueDisabled}
                        saving={navigationSaving}
                        checkingServiceability={checkingServiceability}
                      />
                    </div>
                  )}
                </div>
              </div>

              {!hideNavigation && (
                <div className="hidden md:block shrink-0 px-4 pb-8 pt-2 lg:px-12 lg:pb-12">
                  {showStepError && (
                    <p className="text-sm text-base-red-900 mb-2">{errorMessage}</p>
                  )}
                  <NavigationButtons
                    currentStep={safeStep}
                    onBack={this.handleBack}
                    onContinue={this.handleContinue}
                    isContinueDisabled={continueDisabled}
                    saving={navigationSaving}
                    checkingServiceability={checkingServiceability}
                  />
                </div>
              )}

            </div>
        </div>
    )

    if (isPage) {
      return wizardBody
    }

    return (
      <SharedModal
        id={MODAL_ID}
        isOpen={isOpen}
        maxWidth={modalMaxWidth}
        innerPadding="p-0"
        bgColor="bg-surface"
        wrapperClassName={useOverlayScroll ? 'md:max-h-none md:h-auto' : undefined}
        surfaceClassName={[
          'rounded-20 shadow-md overflow-hidden',
          useOverlayScroll ? 'md:overflow-visible md:h-auto' : '',
        ].join(' ')}
      >
        {wizardBody}
      </SharedModal>
    )
  }
}
